import { createSlice, current } from '@reduxjs/toolkit';
import { shallowEqual } from 'fast-equals';
import { IGrid } from 'store/reducers/TabsSlice';

export interface IQuotesItem {
    [key: string]: {
        [key: string]: number
    }
}

export interface IQuotesState {
    [key: string]: IQuotesItem
}

export type QuotesInfo = {
    [key: string]: number[]
}

export interface UploadedFile {
    name: string
    caller: string | number
    names: string[]
}

export interface IQuotes {
    quotes: IQuotesState
    available_tickers: string[]
    available_tickers_persistent: string[]
    grid: IGrid
    order: number[]
    filter_mnemonics: any[]
    active_page_id: string
    info: {},
    uploaded_file: {
        [key: string]: UploadedFile
    }
    filtered_columns: {
        [key: string]: string[]
    }
    picked_tickers: {
        [key: string]: any[]
    }
    selected_tickers: {
        [key: string]: string[]
    }
}

const initialState: IQuotes = {
    quotes: {},
    available_tickers: [],
    available_tickers_persistent: [],
    picked_tickers: {},
    uploaded_file: {},
    active_page_id: '',
    grid: '2-2',
    filtered_columns: {},
    filter_mnemonics: [],
    selected_tickers: {},
    info: {},
    order: []
}

const quotesSlice = createSlice({
    name: 'quotes',
    initialState,
    reducers: {
        setUploadedFile(state, action) {
            const { caller, names, name } = action.payload;
            state.uploaded_file[caller] = {
                caller,
                names,
                name
            };
        },
        setSelectedTickers(state, action) {
            if (typeof action.payload === 'object' && !Array.isArray(action.payload)) {
                const { data, id } = action.payload;
                state.selected_tickers[id] = data;
                return;
            }
            if (!Array.isArray(action.payload)) {
                return;
            }
            for (const x of action.payload) {
                const { data, id } = x;
                if (!Array.isArray(data) || !id) {
                    continue;
                }
                const obs = current(state.selected_tickers);
                const observable = obs[id];
                const equal = shallowEqual(observable, data);
                const available_tickers = current(state.available_tickers);
                if (!equal) {
                    state.selected_tickers[id] = data.filter(ticker => typeof ticker === 'string');
                }
            }
        },
        setOrder(state, action) {
            const p = action.payload;
            if (Array.isArray(p) && p.length && !p.some(x => typeof x !== 'number')) {
                state.order = p;
            }
        },
        setQuotesInfo(state, action) {
            const { tab_id, workspaces } = action.payload;

            if (tab_id && Array.isArray(workspaces)) {
                state.info[tab_id] = workspaces;
            }

        },
        addFilterMnemonics(state, action) {
            const { mnemonics } = action.payload;
            state.filter_mnemonics = mnemonics;
        },
        resetQuotesState: () => {
            return initialState
        },
        selectFilterColumn(state, action) {
            if (typeof action.payload !== 'object') {
                return;
            }
            let { id, column } = action.payload;

            if (!id || !column) {
                return;
            }
            
            let obs = current(state.filtered_columns);
            if (Array.isArray(obs[id])) {
                if (obs[id].some(x => x.toLowerCase() === column.toLowerCase())) {
                    state.filtered_columns[id] = obs[id].filter(param => param.toLowerCase() !== column.toLowerCase());
                    return;
                }
                state.filtered_columns[id].push(column);
            }
            else {
                state.filtered_columns[id] = [column];
            }
        },
        setFilteredColumns(state, action) {
            if (typeof action.payload === 'object' && action.payload.id) {
                const { id, columns } = action.payload;
                const _obs = current(state.filtered_columns);
                const obs = _obs[id];
                if (Array.isArray(obs) && obs.length) {
                    return;
                }
                state.filtered_columns[id.toString()] = columns;
            }
            if (!Array.isArray(action.payload)) {
                return;
            }
            for (const section of action.payload) {
                const columns = section.columns;
                const id = section.id;
                if (!Array.isArray(columns) || (Array.isArray(columns) && !columns.length) || !id) {
                    continue;
                }
                state.filtered_columns[id] = columns;
            }
        },
        setActivePageId(state, action) {
            if (typeof action.payload !== 'string') {
                return;
            }

            state.active_page_id = action.payload;
        },
        setGrid(state, action) {
            if (typeof action.payload === 'string') {
                //@ts-ignore
                state.grid = action.payload;
            }
        },
        setPickedTickers(state, action) {
            const { id, picked_tickers } = action.payload;
            if (!id || !Array.isArray(picked_tickers)) {
                return;
            }
            state.picked_tickers[id] = picked_tickers;
        },
        setQuotes(state, action) {
            if (typeof action.payload !== 'object') {
                return;
            }
            const { data, reference } = action.payload;

            const new_quotes_state = {};
            const quotes = current(state.quotes);

            for (const param of data) {
                new_quotes_state[param.id] = Object.assign({}, param);
            }

            for (const key of Object.keys(quotes)) {
                const q = quotes[key];
                if (!new_quotes_state[key] && q.reference === reference) {
                    new_quotes_state[key] = q;
                }
            }

            state.quotes = new_quotes_state;

        },
        setAvailableTickers(state, action) {
            if (!Array.isArray(action.payload) || (Array.isArray(action.payload) && !action.payload.length)) {
                return;
            }

            const observable = current(state.available_tickers);
            const persistent = current(state.available_tickers_persistent);
            const is_equal = shallowEqual(observable, action.payload);
            const is_persistent_equal = shallowEqual(persistent, action.payload);

            if (!persistent.length || !is_persistent_equal) {
                state.available_tickers_persistent = action.payload;
            }

            if (!is_equal) {
                state.available_tickers = action.payload;
            }
        }
    }
});

export const {
    setQuotes,
    resetQuotesState,
    setPickedTickers,
    setAvailableTickers,
    setGrid,
    setActivePageId,
    setFilteredColumns,
    selectFilterColumn,
    addFilterMnemonics,
    setQuotesInfo,
    setOrder,
    setSelectedTickers,
    setUploadedFile
} = quotesSlice.actions;

export default quotesSlice.reducer;
